import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import { auth } from '../../../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getAuthErrorMessage, shouldTreatResetAsSent } from '../authFeedback';

const ResetPasswordCard = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(normalizedEmail)) {
      setError('Enter a valid email address to receive the reset link.');
      return;
    }

    setEmail(normalizedEmail);
    setIsLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, normalizedEmail, {
        url: `${window.location.origin}/login`
      });
      setSent(true);
    } catch (err) {
      if (shouldTreatResetAsSent(err)) {
        setSent(true);
      } else {
        setError(getAuthErrorMessage(err, 'reset'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex w-full items-center">
      <Card
        variant="solid"
        hover="none"
        className="w-full rounded-none border-0 bg-white p-6 shadow-none sm:p-10 md:p-12"
      >
        <div className="mb-8">
          <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-[#656C78]">Account recovery</p>
          <h1 className="text-3xl font-semibold tracking-[-0.035em] text-[#17191D]">Reset password</h1>
          <p className="mt-3 text-sm leading-6 text-[#656C78]">
            Enter your email address and we will send a reset link for your portal account.
          </p>
        </div>

        {error && (
          <div id="reset-email-error" className="mb-5 rounded-lg border border-destructive/25 bg-destructive/5 p-4" role="alert" aria-live="polite">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={16} className="mt-0.5 text-destructive" />
              <div>
                <p className="text-sm font-medium text-destructive">Reset email could not be sent</p>
                <p className="mt-1 text-sm text-destructive/90">{error}</p>
              </div>
            </div>
          </div>
        )}

        {sent ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-success/25 bg-success/5 p-4" aria-live="polite">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={16} className="mt-0.5 text-success" />
                <div>
                  <p className="text-sm font-medium text-success">Check your inbox</p>
                  <p className="mt-1 text-sm text-success/90">
                    If an account exists for {email}, a reset link will arrive shortly.
                  </p>
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="default"
              size="lg"
              fullWidth
              onClick={() => navigate('/login')}
              className="rounded-lg bg-[#2457DB] text-white hover:bg-[#1E49BD]"
            >
              Back to Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email"
              id="reset-email"
              name="email"
              type="email"
              placeholder="you@domain.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              required
              disabled={isLoading}
              autoComplete="email"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'reset-email-error' : undefined}
              className="rounded-lg bg-white focus-visible:border-[#2457DB] focus-visible:ring-[#2457DB]/20"
            />

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg bg-[#2457DB] text-white hover:bg-[#1E49BD]"
            >
              Send Reset Email
            </Button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full rounded-sm py-1 text-sm font-medium text-[#2457DB] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2457DB]"
            >
              Back to login
            </button>

            <p className="border-t border-[#DCDFE5] pt-5 text-center text-xs leading-5 text-[#656C78]">
              If you usually sign in with Google, use the Google button instead.
            </p>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ResetPasswordCard;
